import '@fontsource/pt-sans';
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { HelmetProvider } from 'react-helmet-async';

import Router from './Router';
import StoreProvider from './Store/StoreProvider';
import { AppConfig } from './Components';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { bugSnagKey } from './Utils/env';
import { AlertWrapper } from './Components/alerts-manager/AlertsManager';
import { SessionExpiryBanner } from './Components/session-expiry-banner/SessionExpiryBanner';
import { FiveHundred } from './Views/errors/500/FiveHundred';

import './I18n';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.scss';
import { setupAxiosInterceptor } from './services/axiosInterceptor';
import { bootstrapSession } from './services/sessionBootstrap';

setupAxiosInterceptor();

export const bugSnagClient = Bugsnag.start({
	apiKey: bugSnagKey,
	plugins: [new BugsnagPluginReact()],
	enabledReleaseStages: ['production'],
	onError: (event) => {
		const err = event.errors?.[0];
		// Descarta errores originados en scripts inyectados por extensiones del
		// navegador (p. ej. InvalidAccessError en postMessage desde user-script),
		// que no provienen de la app y solo generan ruido en Bugsnag.
		const fromExtension = err?.stacktrace?.some((frame) =>
			/user-script|chrome-extension:\/\/|moz-extension:\/\/|extensions?:\/\//.test(
				frame.file ?? ''
			)
		);
		if (err?.errorClass === 'InvalidAccessError' && fromExtension) {
			return false;
		}
		// Descarta el "Converting circular structure to JSON" que lanzan scripts
		// externos que parchean appendChild y serializan nodos DOM (p. ej. el SVG
		// del logo en /register). El stack proviene de `global code` sin archivo
		// propio, no del bundle de la app.
		const isCircularFromInjected =
			/circular structure to JSON/.test(err?.errorMessage ?? '') &&
			err?.stacktrace?.some(
				(frame) => !frame.file || frame.file === 'global code'
			);
		if (isCircularFromInjected) {
			return false;
		}
	},
});

const ErrorBoundary = bugSnagClient
	.getPlugin('react')
	?.createErrorBoundary(React);

const queryClient = new QueryClient();

function App() {
	// CWE-922: rehidratar la sesión desde la cookie HttpOnly (via /me) antes de
	// resolver rutas, para no actuar sobre el isLogged viejo persistido. Migra
	// además las sesiones legacy (token en localStorage) a cookie sin re-login.
	const [sessionReady, setSessionReady] = React.useState(false);
	React.useEffect(() => {
		bootstrapSession().finally(() => setSessionReady(true));
	}, []);

	return (
		<div className="App">
			<HelmetProvider>
				<QueryClientProvider client={queryClient}>
					<StoreProvider>
						<AppConfig>
							{sessionReady ? (
								<Router />
							) : (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										minHeight: '100vh',
									}}
								>
									<div
										className="spinner-border"
										role="status"
										aria-label="Cargando"
									/>
								</div>
							)}
							<AlertWrapper />
							<SessionExpiryBanner />
						</AppConfig>
					</StoreProvider>
					<ReactQueryDevtools position="bottom-right" />
				</QueryClientProvider>
			</HelmetProvider>
		</div>
	);
}

const BugsnagApp = () => {
	if (ErrorBoundary && import.meta.env.PROD) {
		return (
			<ErrorBoundary FallbackComponent={FiveHundred}>
				<App />
			</ErrorBoundary>
		);
	}

	return <App />;
};

export default BugsnagApp;
